import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { saveToStorage } from '../helpers/storageUtils';
import { DatosGenerales } from '../Models/datosGenerales';
import { DatosPlanes } from '../Models/datosPlanes';
import { LoaderService } from '../services/loader.service';
import { ERROR_500 } from '../shared/final-pages/final-pages.constants';
import { RetakeService } from '../shared/retake/retake.service';
import {
  mapRetakeDataToLeadRequest,
  mapRetakeDataToLeadResponse,
  mapRetakeDataToPlanesSelection,
  mapRetakeDataToRemainingForm
} from '../shared/retake/retake.utils';
import { loadCotizacion, updateResponse } from '../storage/Lead/data.actions';
import { loadPlanes } from '../storage/Planes/planes.action';

/**
 * Guard funcional para procesar el parámetro recordId de la URL en la app de motos.
 * Si existe el parámetro, inicia el proceso de recuperación de datos del lead.
 *
 * A diferencia de la app de autos, este guard utiliza NgRx Store para gestionar el estado.
 */
export const leadResumeGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean> => {
  const retakeService = inject(RetakeService);
  const store = inject(Store);
  const router = inject(Router);
  const loaderService = inject(LoaderService);

  const recordId = route.queryParamMap.get('recordId');

  // Si no hay recordId, permitir navegación normal
  if (!recordId) {
    return of(true);
  }

  console.log('🔄 Guard de retoma activado con recordId:', recordId);
  loaderService.showLoader();

  // Llamar al servicio de retake para obtener los datos del lead
  return retakeService.getRetakeData(recordId).pipe(
    map(response => {
      loaderService.hideLoader();
      if (response && response.data) {
        console.log('✅ Datos de retoma recibidos:', response.data);

        // Mapear los datos de retoma a las interfaces de motos
        const leadRequestData = mapRetakeDataToLeadRequest(response.data);
        const leadResponseData = mapRetakeDataToLeadResponse(response.data);

        console.log('📦 Lead Request mapeado:', leadRequestData);
        console.log('📦 Lead Response mapeado:', leadResponseData);

        // Construir el objeto DatosGenerales completo
        const datosGenerales: DatosGenerales = {
          Lead: leadRequestData as any, // El mapeo retorna Partial<CreateLead>
          LeadResponse: leadResponseData
        };

        // Despachar acciones al store para guardar los datos
        store.dispatch(loadCotizacion({ data: datosGenerales }));
        store.dispatch(updateResponse({ leadR: leadResponseData }));

        console.log('✅ Datos guardados en NgRx Store');

        // Guardar datos del formulario Datos-Restantes en sessionStorage para pre-llenado
        const remainingFormData = mapRetakeDataToRemainingForm(response.data);
        if (remainingFormData) {
          saveToStorage('remainingDataForm', remainingFormData, 'session');
          // Marcar que hay intentos de SARLAFT para que el formulario cargue desde storage
          saveToStorage('sarlaftAttemps', 1, 'session');
          console.log('💾 Datos del formulario Datos-Restantes guardados en sessionStorage');
        }

        // Mapear y guardar datos del plan seleccionado
        const planesSelection = mapRetakeDataToPlanesSelection(response.data);
        if (planesSelection) {
          store.dispatch(loadPlanes({ data: planesSelection as DatosPlanes }));
          // También guardar en sessionStorage para compatibilidad con componentes que lo leen directamente
          saveToStorage('selectedPlan', planesSelection.selected_plan, 'session');
          saveToStorage('paymentFrequency', planesSelection.payment_frequency, 'session');
          console.log('📋 Datos de plan seleccionado guardados:', {
            plan: planesSelection.selected_plan,
            frequency: planesSelection.payment_frequency
          });
        }

        // Permitir la navegación
        return true;
      } else {
        console.error('❌ Respuesta de retoma inválida:', response);
        router.navigate([ERROR_500]);
        return false;
      }
    }),
    catchError(error => {
      loaderService.hideLoader();
      console.error('❌ Error al obtener datos de retoma:', error);
      router.navigate([ERROR_500]);
      return of(false);
    })
  );
};
